import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ToursCardsSection } from "../components/tours-cards-section/ToursCardsSection";
import { ToursCreateFilters } from "../components/tours-create-filters/ToursCreateFilters";
import { ToursHeader } from "../components/tours-header/ToursHeader";
import {
  getCountries,
  getHistory,
  type Country,
  type TourHistoryDay,
} from "../api/ToursApi";
import { useToursFilter } from "../hooks/useToursFilter";
import { useHistoryPolling } from "../hooks/useHistoryPolling";
import { mapHistoryToGroups } from "../utils/mapHistoryToGroups";

// Бэкенд группирует историю по дням, пагинация — постранично по дням.
const PAGE_SIZE_DAYS = 7;

export function ToursPage() {
  const { filters, setFilters, save, isSaving } = useToursFilter();

  const [countries, setCountries] = useState<Country[]>([]);
  const [days, setDays] = useState<TourHistoryDay[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    getCountries()
      .then(setCountries)
      .catch((error) => {
        toast.error(
          error instanceof Error ? error.message : "Не удалось загрузить страны"
        );
      });
  }, []);

  const fetchHistory = useCallback(() => {
    getHistory({ page, pageSize: PAGE_SIZE_DAYS })
      .then((response) => {
        setDays(response.data);
        setTotalPages(response.totalPages);
      })
      .catch((error) => {
        toast.error(
          error instanceof Error
            ? error.message
            : "Не удалось загрузить горящие предложения"
        );
      });
  }, [page]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const groups = useMemo(() => mapHistoryToGroups(days), [days]);

  const isProcessing = useMemo(
    () =>
      groups.some((g) =>
        Object.values(g.slots).some((slot) => slot.card?.status === "pending")
      ),
    [groups]
  );

  useHistoryPolling(fetchHistory, isProcessing);

  const handleSave = useCallback(async () => {
    try {
      await save();
      fetchHistory(); // показываем только что запущенный прогон
    } catch {
      // тост уже показан в save()
    }
  }, [save, fetchHistory]);

  return (
    <div className="p-6 space-y-6">
      <ToursHeader />

      <ToursCreateFilters
        filters={filters}
        setFilters={setFilters}
        countries={countries}
        onSave={handleSave}
        isSaving={isSaving}
      />

      {days.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Фильтр ещё не задан. Настройте параметры выше и нажмите «Сохранить
          фильтр» — поиск будет запускаться автоматически три раза в день.
        </p>
      )}

      <ToursCardsSection
        groups={groups}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
}
